'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Copy, CheckCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

const demoAccounts = [
  { role: 'Employer', email: 'grace.moyo@cvmaids.co.zw', password: 'Employer@2026', redirect: '/welcome' },
  { role: 'Home Manager', email: 'zanele.dube@cvmaids.co.zw', password: 'HomeManager@2026', redirect: '/welcome' },
  { role: 'Admin', email: 'admin@cvmaids.co.zw', password: 'Admin@2026', redirect: '/welcome' },
];

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { remember: false } });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in successfully! Redirecting...');
      router.push('/welcome');
    } catch (err: any) {
      setError('email', {
        message: err?.message || 'Invalid credentials. Please check your email and password.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseDemoAccount = (account: typeof demoAccounts[0]) => {
    setValue('email', account.email);
    setValue('password', account.password);
  };

  const inputClass = "w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all placeholder:text-slate-400 text-slate-800";
  const inputStyle = { borderColor: 'rgba(232,93,4,0.20)' };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          Email Address
        </label>
        <input
          type="email"
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
          placeholder="your@email.com"
          className={inputClass}
          style={inputStyle}
          suppressHydrationWarning
        />
        {errors.email && (
          <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Password</label>
          <button type="button" className="text-xs text-primary hover:underline" suppressHydrationWarning>
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            {...register('password', { required: 'Password is required' })}
            placeholder="••••••••"
            className={`${inputClass} pr-11`}
            style={inputStyle}
            suppressHydrationWarning
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            suppressHydrationWarning
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1.5 text-xs text-red-600">{errors.password.message}</p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="remember"
          {...register('remember')}
          className="w-4 h-4 rounded border-orange-200 text-primary focus:ring-primary/30 cursor-pointer"
          suppressHydrationWarning
        />
        <label htmlFor="remember" className="text-sm text-slate-500 cursor-pointer">
          Keep me signed in
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 text-white text-sm font-bold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
        style={{ minHeight: '44px', background: 'linear-gradient(135deg, #F47B20 0%, #e8650a 100%)', boxShadow: '0 4px 14px rgba(232,93,4,0.30)' }}
        suppressHydrationWarning
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In'
        )}
      </button>

      <div className="text-center text-sm text-slate-500">
        Don&apos;t have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="text-primary font-semibold hover:underline"
        >
          Register now
        </button>
      </div>

      {/* Demo credentials */}
      <div className="mt-4 rounded-xl overflow-hidden" style={{border: '1px solid rgba(232,93,4,0.15)', background: 'rgba(248,244,239,0.6)'}}>
        <div className="px-4 py-2.5 border-b" style={{borderColor: 'rgba(232,93,4,0.12)', background: 'rgba(232,93,4,0.06)'}}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Demo Accounts</p>
        </div>
        <div className="divide-y" style={{borderColor: 'rgba(232,93,4,0.10)'}}>
          {demoAccounts.map((account) => (
            <div key={`demo-${account.role}`} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-700">{account.role}</p>
                <p className="text-xs text-slate-400 truncate">{account.email}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleCopy(account.email, `email-${account.role}`)}
                  className="p-1.5 rounded hover:bg-orange-50 transition-all"
                  title="Copy email"
                >
                  {copiedField === `email-${account.role}` ? (
                    <CheckCheck size={14} className="text-green-600" />
                  ) : (
                    <Copy size={14} className="text-slate-400" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleUseDemoAccount(account)}
                  className="px-3 py-1.5 text-primary text-xs font-semibold rounded-lg transition-all active:scale-95"
                  style={{background: 'rgba(232,93,4,0.10)', border: '1px solid rgba(232,93,4,0.18)'}}
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}